import type { ReactElement, Ref } from 'react';
import { useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DECISION_PRIORITIES,
  type DecisionPriority,
  DEFAULT_DECISION_PRIORITY,
  PROCESS_DECISION_COMMENT_MAX_LENGTH,
} from '@saas/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown } from '@/components/Dropdown';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import {
  fetchStartPool,
  type ProcessSummary,
  type StartPoolNode,
} from '@/lib/processes/instance-api';

/**
 * Uchwyt do sterowania oknem z zewnątrz. Rodzic zna wynik wysyłki (a wybór procesu i klucz puli
 * trzyma okno), więc to rodzic mówi „pula jest nieaktualna", a okno wie, co i jak odświeżyć —
 * klucz zapytania o pulę zostaje w jednym miejscu.
 */
export interface ProcessPickerModalHandle {
  /**
   * Wymusza świeże pobranie puli akceptantów dla wskazanego procesu (i rekordu okna) — omija
   * `staleTime`, a okno zostaje otwarte z niezmienionym komentarzem, priorytetem i terminem.
   * Wybory adresatów, które po odświeżeniu przestały być poprawne, okno samo zeruje.
   */
  refreshPool: (processId: string) => void;
}

export interface ProcessPickerModalProps {
  open: boolean;
  processes: ProcessSummary[];
  /** Rekord, dla którego startuje się proces — potrzebny do podglądu puli adresatów (F.4). */
  targetRecordId: string;
  /** Uchwyt `refreshPool` — patrz `ProcessPickerModalHandle`. */
  ref?: Ref<ProcessPickerModalHandle>;
  submitting?: boolean;
  onConfirm: (
    processId: string,
    comment: string,
    /** Priorytet/pilność wniosku — wybór przy starcie procesu. */
    priority: DecisionPriority,
    /** Termin (SLA, migracja 0126) — ISO datetime string; `undefined` = bez terminu. */
    deadlineAt: string | undefined,
    /** F.4 (ADR-0073) — wskazania adresatów dla flagowanych węzłów startowych. */
    addressees: Array<{ nodeId: string; userId: string }>,
  ) => void;
  onClose: () => void;
  testIdPrefix?: string;
}

/**
 * `YYYY-MM-DD` (wartość `DatePicker`) → ISO datetime KOŃCA wybranego dnia (23:59:59.999
 * czasu LOKALNEGO, dopiero potem `toISOString()`). Termin to „do końca dnia X" — naiwna
 * konkatenacja `${v}T00:00:00Z` (wzorzec z `contractorEngineConfig.tsx`) dałaby północ
 * na starcie dnia, więc proces wyglądałby na przeterminowany już rano dnia terminu.
 * Budowa przez komponenty `Date` (nie string) unika też przesunięcia dnia w strefach
 * spoza UTC. Puste `''` → `undefined` (backend: pominięte pole = bez terminu).
 */
function deadlineDateToIso(value: string): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return undefined;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), 23, 59, 59, 999).toISOString();
}

/** Klucz zapytania o pulę akceptantów pierwszego etapu — jeden dla odczytu i dla unieważnienia. */
function startPoolQueryKey(
  processId: string,
  targetRecordId: string,
): readonly ['processes', 'start-pool', string, string] {
  return ['processes', 'start-pool', processId, targetRecordId] as const;
}

/**
 * Wybory adresatów po (od)świeżeniu puli: zostają tylko te, które nadal są poprawne — węzeł jest
 * w puli, nadal wymaga wskazania adresata, a wybrana osoba nadal jest wśród jego akceptantów.
 * Reszta znika, żeby użytkownik wybrał ponownie (inaczej „Uruchom" wysłałby nieaktualną osobę).
 * Zwraca ten sam obiekt, gdy nic nie trzeba usuwać (bez zbędnego renderu).
 */
function pruneInvalidAddresseeSelections(
  selections: Record<string, string>,
  pool: StartPoolNode[],
): Record<string, string> {
  const next: Record<string, string> = {};
  let changed = false;
  for (const [nodeId, userId] of Object.entries(selections)) {
    const node = pool.find((n) => n.nodeId === nodeId);
    if (node?.requiresDesignation && node.approvers.some((a) => a.id === userId)) {
      next[nodeId] = userId;
    } else {
      changed = true;
    }
  }
  return changed ? next : selections;
}

/**
 * Modal wyboru procesu + opcjonalny komentarz inicjatora (F17 T17.1.7).
 * Kontrolowany: rodzic (SubmitForApprovalButton) trzyma listę procesów i wykonuje
 * mutację startu w `onConfirm(processId, comment, addressees)`. Komentarz renderowany
 * jako zwykły input — wartość trafia do backendu, nigdy nie jest interpretowana jako HTML.
 * Długość komentarza ogranicza `PROCESS_DECISION_COMMENT_MAX_LENGTH` (ta sama stała co w walidatorze
 * serwera i w oknie „Akceptacja") — twardo, z licznikiem „N / limit" pod polem.
 *
 * F.4 (ADR-0073) — po wybraniu procesu dociągamy `GET /processes/:id/start-pool` (żywy
 * graf, nie zamrożony snapshot — instancja jeszcze nie istnieje); dla węzłów
 * `requiresDesignation` renderujemy wymagany picker adresata PRZED startem.
 *
 * Gdy serwer odmówi startu, bo ustawienia etapu albo pula akceptantów zmieniły się po otwarciu
 * okna, rodzic woła `refreshPool` (uchwyt `ref`): okno zostaje otwarte, pula pobiera się od nowa,
 * a nieaktualne wybory adresatów znikają.
 */
export function ProcessPickerModal({
  open,
  processes,
  targetRecordId,
  ref,
  submitting = false,
  onConfirm,
  onClose,
  testIdPrefix = 'process-picker',
}: ProcessPickerModalProps): ReactElement | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState('');
  const [comment, setComment] = useState('');
  const [priority, setPriority] = useState<DecisionPriority>(DEFAULT_DECISION_PRIORITY);
  const [deadline, setDeadline] = useState('');
  const [addresseeSelections, setAddresseeSelections] = useState<Record<string, string>>({});

  // Reset wyboru/komentarza/priorytetu/terminu przy otwarciu (domyślnie pierwszy proces).
  useEffect(() => {
    if (open) {
      setSelectedId(processes[0]?.id ?? '');
      setComment('');
      setPriority(DEFAULT_DECISION_PRIORITY);
      setDeadline('');
      setAddresseeSelections({});
    }
  }, [open, processes]);

  const poolQuery = useQuery({
    queryKey: startPoolQueryKey(selectedId, targetRecordId),
    queryFn: () => fetchStartPool(selectedId, targetRecordId),
    enabled: open && !!selectedId && !!targetRecordId,
    staleTime: 30_000,
  });

  // Unieważnienie (nie `refetch`): omija `staleTime`, odświeża od razu tylko zapytanie, które
  // okno aktualnie obserwuje (otwarte okno), a przy zamkniętym oknie oznacza pulę jako starą —
  // następne otwarcie pobierze ją od nowa zamiast pokazać 30-sekundową kopię.
  useImperativeHandle(
    ref,
    () => ({
      refreshPool: (processId: string) => {
        void queryClient.invalidateQueries({
          queryKey: startPoolQueryKey(processId, targetRecordId),
        });
      },
    }),
    [queryClient, targetRecordId],
  );

  // Zmiana wybranego procesu → stara pula/wybory nieaktualne.
  useEffect(() => {
    setAddresseeSelections({});
  }, [selectedId]);

  // Po każdym nowym stanie puli: najpierw odsiewamy wybory, które przestały być poprawne
  // (odświeżenie po odmowie startu), potem preselekcja domyślnego adresata (gdy obecny w puli),
  // bez nadpisywania ręcznego, nadal poprawnego wyboru.
  useEffect(() => {
    const pool = poolQuery.data;
    if (!pool) return;
    setAddresseeSelections((prev) => {
      const pruned = pruneInvalidAddresseeSelections(prev, pool);
      let changed = pruned !== prev;
      const next = { ...pruned };
      for (const node of pool) {
        if (
          node.requiresDesignation &&
          !next[node.nodeId] &&
          node.defaultAddresseeUserId &&
          node.approvers.some((a) => a.id === node.defaultAddresseeUserId)
        ) {
          next[node.nodeId] = node.defaultAddresseeUserId;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [poolQuery.data]);

  const flaggedNodes = (poolQuery.data ?? []).filter((n) => n.requiresDesignation);
  const allAddresseesSelected = flaggedNodes.every((n) => !!addresseeSelections[n.nodeId]);
  // `poolQuery.isFetching` blokuje Uruchom dopóki pula (i ewentualne flagi wymogu adresata)
  // się nie dociągnie — bez tego byłoby okienko czasowe, w którym user mógłby kliknąć
  // Uruchom PRZED poznaniem że węzeł startowy wymaga wskazania adresata (obejście hard blocka).
  // `isFetching` (nie `isLoading`) pilnuje też odświeżenia po odmowie startu: do czasu nowej
  // puli nie da się ponownie wysłać nieaktualnego wyboru.
  const canConfirm =
    selectedId !== '' && !submitting && !poolQuery.isFetching && allAddresseesSelected;
  const single = processes.length === 1 ? processes[0] : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.processes.picker.title', 'Wyślij do akceptacji')}
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            size="sm"
            data-testid={`${testIdPrefix}-cancel`}
            onClick={onClose}
          >
            {t('common.cancel', 'Anuluj')}
          </Button>
          <Button
            size="sm"
            data-testid={`${testIdPrefix}-confirm`}
            disabled={!canConfirm}
            onClick={() =>
              onConfirm(
                selectedId,
                comment,
                priority,
                deadlineDateToIso(deadline),
                flaggedNodes.map((n) => ({
                  nodeId: n.nodeId,
                  userId: addresseeSelections[n.nodeId] ?? '',
                })),
              )
            }
          >
            {t('tenant.processes.picker.confirm', 'Uruchom')}
          </Button>
        </>
      }
    >
      {processes.length > 1 ? (
        <label className="block">
          <span className="mb-1 block text-text-secondary">
            {t('tenant.processes.picker.selectProcess', 'Proces')}
          </span>
          <Dropdown
            data-testid={`${testIdPrefix}-process-select`}
            value={selectedId}
            onChange={(value) => setSelectedId(value)}
            options={processes.map((p) => ({ value: p.id, label: p.name }))}
          />
        </label>
      ) : (
        <div data-testid={`${testIdPrefix}-process-single`} className="font-medium">
          {single?.name}
        </div>
      )}

      <label className="mt-3 block">
        <span className="mb-1 block text-text-secondary">
          {t('tenant.processes.picker.priority', 'Priorytet')}
        </span>
        <Dropdown
          data-testid={`${testIdPrefix}-priority-select`}
          value={priority}
          onChange={(value) => setPriority(value as DecisionPriority)}
          options={DECISION_PRIORITIES.map((p) => ({
            value: p,
            label: t(`tenant.processes.priorityLabel.${p}`),
          }))}
        />
      </label>

      <label className="mt-3 block">
        <span className="mb-1 block text-text-secondary">
          {t('tenant.processes.picker.deadline')}
        </span>
        <DatePicker
          data-testid={`${testIdPrefix}-deadline`}
          value={deadline}
          onChange={setDeadline}
          placeholder={t('tenant.processes.picker.deadlinePlaceholder')}
          aria-label={t('tenant.processes.picker.deadline')}
        />
        <span className="mt-1 block text-xs text-text-tertiary">
          {t('tenant.processes.picker.deadlineHelp')}
        </span>
      </label>

      <Textarea
        containerClassName="mt-3"
        label={t('tenant.processes.picker.comment', 'Komentarz (opcjonalny)')}
        data-testid={`${testIdPrefix}-comment`}
        maxLength={PROCESS_DECISION_COMMENT_MAX_LENGTH}
        showCount
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
      />

      {flaggedNodes.length > 0 && (
        <div className="mt-3 space-y-3" data-testid={`${testIdPrefix}-addressees`}>
          {flaggedNodes.map((n) => (
            <label
              key={n.nodeId}
              className="block"
              data-testid={`${testIdPrefix}-addressee-${n.nodeId}`}
            >
              <span className="mb-1 block text-text-secondary">
                {t('tenant.processes.picker.addressee.label')}
              </span>
              <Dropdown
                data-testid={`${testIdPrefix}-addressee-${n.nodeId}-select`}
                value={addresseeSelections[n.nodeId] ?? ''}
                onChange={(value) =>
                  setAddresseeSelections((prev) => ({ ...prev, [n.nodeId]: value }))
                }
                options={[
                  { value: '', label: t('tenant.processes.picker.addressee.selectPlaceholder') },
                  ...n.approvers.map((a) => ({ value: a.id, label: a.displayName })),
                ]}
              />
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}
